import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import ErrorPopup from "./ErrorPopup";
import SuccessPopup from "./SuccessPopup";
import {
  UserPlus,
  Mail,
  Lock,
  User,
  MapPin,
  GraduationCap,
  Users,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Shield,
  BookOpen,
  Heart,
  Phone,
  IdCard,
  Calendar,
  Home,
  Building,
  DollarSign,
  Eye,
  EyeOff,
} from "lucide-react";

const tabs = [
  { label: "Account", icon: <Lock className="w-4 h-4" /> },
  { label: "Personal", icon: <User className="w-4 h-4" /> },
  { label: "Address", icon: <MapPin className="w-4 h-4" /> },
  { label: "University", icon: <GraduationCap className="w-4 h-4" /> },
  { label: "Next of Kin", icon: <Users className="w-4 h-4" /> },
];

export default function StudentRegistrationForm() {
  const [activeTab, setActiveTab] = useState(0);
  const [Universities, setUniversities] = useState([]);
  const [residences, setResidences] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    createEmail: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    idNumber: "",
    email: "",
    phone: "",
    studentNumber: "",
    medicalConditions: "",
    streetAddress: "",
    university: "",
    Residence: "",
    fieldOfStudy: "",
    yearOfStudy: "",
    kinFirstName: "",
    kinLastName: "",
    kinEmail: "",
    kinPhone: "",
    kinIdNumber: "",
    kinRelationship: "",
    kinHomeAddress: "",
    userId: "",
    paymentMethod: "",
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "YourGoogle_Api_Map_Key",
    libraries: ["places"],
  });
  const autocompleteRef = React.useRef(null);

  const navigate = useNavigate();
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await axios.get(
          "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/universities"
        );
        setUniversities(response.data);
      } catch (error) {
        console.error("Error fetching universities:", error);
      }
    };
    fetchUniversities();
  }, []);

  const fetchResidences = async (UniversityId) => {
    try {
      const response = await axios.get(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/universities/residences?universityId=${UniversityId}`
      );
      setResidences(response.data);
    } catch (error) {
      console.error("Error fetching residences:", error);
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (activeTab === 0) {
      if (!formData.createEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.createEmail))
        newErrors.createEmail = "A valid email is required.";
      if (formData.createEmail !== formData.confirmEmail)
        newErrors.confirmEmail = "Emails do not match.";
      if (!formData.password)
        newErrors.password = "Password is required.";
      if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = "Passwords do not match.";
    }
    if (activeTab === 1) {
      if (!formData.firstName) newErrors.firstName = "First name is required.";
      if (!formData.lastName) newErrors.lastName = "Last name is required.";
      if (!formData.gender) newErrors.gender = "Gender is required.";
      if (!formData.idNumber || !/^\d{1,13}$/.test(formData.idNumber))
        newErrors.idNumber = "Valid ID number (up to 13 digits) is required.";
      if (!formData.phone || !/^\d{10}$/.test(formData.phone))
        newErrors.phone = "Phone number must be 10 digits.";
      if (!formData.studentNumber)
        newErrors.studentNumber = "Student number is required.";
    }
    if (activeTab === 2) {
      if (!formData.streetAddress) newErrors.streetAddress = "Street address is required.";
    }
    if (activeTab === 3) {
      if (!formData.university) newErrors.university = "University selection is required.";
      if (!formData.Residence) newErrors.Residence = "Residence selection is required.";
      if (!formData.fieldOfStudy) newErrors.fieldOfStudy = "Field of study is required.";
      if (!formData.yearOfStudy) newErrors.yearOfStudy = "Year of study is required.";
    }
    if (activeTab === 4) {
      if (!formData.kinFirstName) newErrors.kinFirstName = "Next of kin first name is required.";
      if (!formData.kinLastName) newErrors.kinLastName = "Next of kin last name is required.";
      if (!formData.kinEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.kinEmail))
        newErrors.kinEmail = "Valid next of kin email is required.";
      if (!formData.kinPhone || !/^\d{10}$/.test(formData.kinPhone))
        newErrors.kinPhone = "Next of kin phone must be 10 digits.";
      if (!formData.kinIdNumber || !/^\d{1,13}$/.test(formData.kinIdNumber))
        newErrors.kinIdNumber = "Valid next of kin ID is required.";
      if (!formData.kinRelationship) newErrors.kinRelationship = "Relationship is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyEmail = async () => {
    let emailErrors = {};
    if (!formData.createEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.createEmail))
      emailErrors.createEmail = "A valid email is required.";
    if (formData.createEmail !== formData.confirmEmail)
      emailErrors.confirmEmail = "Emails do not match.";
    if (!formData.password)
      emailErrors.password = "Password is required.";
    else if (passwordErrors.length > 0)
      emailErrors.password = "Password is not strong enough.";
    if (formData.password !== formData.confirmPassword)
      emailErrors.confirmPassword = "Passwords do not match.";
    if (Object.keys(emailErrors).length > 0) {
      setErrors(emailErrors);
      setShowErrorPopup(true);
      return;
    }
    setLoading(true);
    const loadingTimeout = setTimeout(() => setLoading(false), 7000);
    try {
      const response = await axios.post(
        "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/accounts/register",
        { email: formData.createEmail, password: formData.password }
      );
      setFormData({ ...formData, userId: response.data.userId });
      await axios.post(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/verify-email?email=${formData.createEmail}`
      );
      setSuccessMessage(
        `A verification link has been sent to ${formData.createEmail}. Please check your inbox.`
      );
      setShowSuccessPopup(true);
      const intervalId = setInterval(async () => {
        try {
          const res = await axios.get(
            `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/is-verified?email=${formData.createEmail}`
          );
          if (res.data.isVerified) {
            clearInterval(intervalId);
            setIsEmailVerified(true);
            setShowSuccessPopup(false);
            setSuccessMessage("Email verified successfully!");
            setShowSuccessPopup(true);
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 5000);
    } catch (error) {
      console.error("Error creating account:", error);
      if (error.response && error.response.status === 409) {
        try {
          const userRes = await axios.get(
            `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/user-id?email=${formData.createEmail}`
          );
          setFormData((prev) => ({ ...prev, userId: userRes.data.userId }));
          const res = await axios.get(
            `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/is-verified?email=${formData.createEmail}`
          );
          if (!res.data.isVerified) {
            await axios.post(
              `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/verify-email?email=${formData.createEmail}`
            );
            setSuccessMessage(
              `This email is already registered but not yet verified. A new verification link has been sent to ${formData.createEmail}.`
            );
            setShowSuccessPopup(true);
            const intervalId = setInterval(async () => {
              try {
                const check = await axios.get(
                  `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/is-verified?email=${formData.createEmail}`
                );
                if (check.data.isVerified) {
                  clearInterval(intervalId);
                  setIsEmailVerified(true);
                  setSuccessMessage("Email verified successfully!");
                  setShowSuccessPopup(true);
                }
              } catch (err) {
                console.error("Polling error:", err);
              }
            }, 5000);
          } else {
            setErrors({ createEmail: "An account with this email already exists." });
            setShowErrorPopup(true);
          }
        } catch (checkError) {
          console.error("Verification check error:", checkError);
          alert("Error checking email status. Please try again.");
        }
        clearTimeout(loadingTimeout);
        setLoading(false);
        return;
      }
      alert("Error creating account. Please try again.");
    }
  };

  const validatePasswordStrength = (password) => {
    const rules = [
      { regex: /.{8,}/, message: "At least 8 characters" },
      { regex: /[A-Z]/, message: "At least one uppercase letter" },
      { regex: /[a-z]/, message: "At least one lowercase letter" },
      { regex: /[0-9]/, message: "At least one number" },
      { regex: /[^A-Za-z0-9]/, message: "At least one special character" },
    ];
    const failedRules = rules.filter((rule) => !rule.regex.test(password)).map((rule) => rule.message);
    setPasswordErrors(failedRules);
  };

  const handleCreateAccount = async () => {
    let emailErrors = {};
    if (!formData.createEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.createEmail))
      emailErrors.createEmail = "Please enter a valid email address.";
    if (formData.createEmail !== formData.confirmEmail)
      emailErrors.confirmEmail = "Emails do not match.";
    if (!formData.password)
      emailErrors.password = "Password is required.";
    else if (passwordErrors.length > 0)
      emailErrors.password = "Password is not strong enough.";
    if (formData.password !== formData.confirmPassword)
      emailErrors.confirmPassword = "Passwords do not match.";
    if (Object.keys(emailErrors).length > 0) {
      setErrors(emailErrors);
      setShowErrorPopup(true);
      return;
    }
    try {
      const res = await axios.get(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/is-verified?email=${formData.createEmail}`
      );
      if (!res.data.isVerified) {
        setErrors({ verify: "Please verify your email before continuing." });
        setShowErrorPopup(true);
        setIsEmailVerified(false);
        return;
      }
      setIsEmailVerified(true);
      setActiveTab(1);
    } catch (error) {
      console.error("Error checking verification:", error);
      setErrors({ verify: "Error verifying email. Please try again." });
      setShowErrorPopup(true);
    }
  };

  const handleNext = async () => {
    const lettersOnly = /^[A-Za-z]+$/;
    const lettersOptional = /^[A-Za-z]*$/;
    const digitsMax13 = /^\d{1,13}$/;
    const phoneRegex = /^\d{10}$/;
    let frontErrors = {};

    if (activeTab === 1) {
      if (!formData.firstName?.trim() || !lettersOnly.test(formData.firstName))
        frontErrors.firstName = "First name must contain only letters.";
      if (!lettersOptional.test(formData.middleName || ""))
        frontErrors.middleName = "Middle name must contain only letters.";
      if (!formData.lastName?.trim() || !lettersOnly.test(formData.lastName))
        frontErrors.lastName = "Last name must contain only letters.";
      if (!formData.gender)
        frontErrors.gender = "Gender is required.";
      if (!digitsMax13.test(formData.idNumber || ""))
        frontErrors.idNumber = "ID number must be up to 13 digits.";
      if (!phoneRegex.test(formData.phone || ""))
        frontErrors.phone = "Phone number must be exactly 10 digits.";
      if (!formData.studentNumber?.trim())
        frontErrors.studentNumber = "Student number is required.";
    }

    if (Object.keys(frontErrors).length > 0) {
      setErrors(frontErrors);
      setShowErrorPopup(true);
      return;
    }

    const userId = formData.userId;
    try {
      if (activeTab === 1) {
        await axios.post(
          `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/users/${userId}/personal-info`,
          {
            firstName: formData.firstName?.trim(),
            middleName: formData.middleName?.trim() || "",
            lastName: formData.lastName?.trim(),
            gender: formData.gender,
            idNumber: formData.idNumber?.trim(),
            email: formData.createEmail?.trim().toLowerCase(),
            phone: formData.phone?.trim(),
            studentNumber: formData.studentNumber?.trim(),
            medicalConditions: formData.medicalConditions?.trim() || "",
          }
        );
      }
      if (activeTab === 2) {
        await axios.post(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/users/${userId}/address`, {
          streetAddress: formData.streetAddress,
        });
      }
      if (activeTab === 3) {
        await axios.post(
          `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/users/${userId}/university-detail`,
          {
            UniversityId: formData.university,
            residenceId: formData.Residence,
            fieldOfStudy: formData.fieldOfStudy,
            yearOfStudy: formData.yearOfStudy,
            paymentMethod: formData.paymentMethod,
          }
        );
      }
      setActiveTab((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving data:", error);
      if (error.response?.status === 400 && error.response.data?.Errors) {
        const backendErrors = error.response.data.Errors;
        const mappedErrors = backendErrors.reduce((acc, msg) => {
          acc.general = msg;
          return acc;
        }, {});
        setErrors(mappedErrors);
        setShowErrorPopup(true);
      } else {
        alert("Error saving data. Please try again.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setShowErrorPopup(true);
      return;
    }
    const userId = formData.userId;
    try {
      await axios.post(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/users/${userId}/next-of-kin`,
        {
          nextOfKinName: formData.kinFirstName,
          nextOfKinLastName: formData.kinLastName,
          nextOfKinEmail: formData.kinEmail,
          nextOfKinPhone: formData.kinPhone,
          nextOfKinIdNumber: formData.kinIdNumber,
          relationship: formData.kinRelationship,
          nextOfKinHomeAddress: formData.kinHomeAddress,
        }
      );
      setSuccessMessage(
        "🎉 Congratulations! Your student profile has been created. Please log in and upload your required documents."
      );
      setShowSuccessPopup(true);
      setTimeout(() => navigate("/login"), 4000);
    } catch (error) {
      console.error("Error saving Next of Kin:", error);
      setErrors({ submit: "Error saving next of kin information. Please try again." });
      setShowErrorPopup(true);
    }
  };

  const inputClass = `
    w-full px-4 py-3 text-sm text-gray-800 bg-white
    border-2 border-gray-200 rounded-xl
    placeholder:text-gray-400
    focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
    transition-all duration-200
    disabled:bg-gray-50 disabled:cursor-not-allowed
  `;

  const progressPercent = Math.round(((activeTab + 1) / tabs.length) * 100);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email address <span className="text-red-500">*</span></label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                <input
                  name="createEmail" type="email" placeholder="student@university.ac.za"
                  value={formData.createEmail}
                  onChange={(e) => setFormData({ ...formData, createEmail: e.target.value })}
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm email address <span className="text-red-500">*</span></label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                <input
                  name="confirmEmail" type="email" placeholder="Repeat your email address"
                  value={formData.confirmEmail}
                  onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"} name="password" placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => { setFormData({ ...formData, password: e.target.value }); validatePasswordStrength(e.target.value); }}
                  className={`${inputClass} pl-10 pr-10`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                  <p className="text-xs font-semibold text-indigo-900 mb-2">Password requirements</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {[
                      { regex: /.{8,}/, msg: "At least 8 characters" },
                      { regex: /[A-Z]/, msg: "One uppercase letter" },
                      { regex: /[a-z]/, msg: "One lowercase letter" },
                      { regex: /[0-9]/, msg: "One number" },
                      { regex: /[^A-Za-z0-9]/, msg: "One special character" },
                    ].map((rule, idx) => {
                      const passed = rule.regex.test(formData.password);
                      return (
                        <div key={idx} className={`flex items-center gap-1.5 text-xs ${passed ? "text-emerald-600" : "text-red-500"}`}>
                          {passed ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                          <span>{rule.msg}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm password <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"} name="confirmPassword" placeholder="Repeat your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`${inputClass} pl-10 pr-10`}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors">
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleVerifyEmail} disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-semibold hover:bg-slate-800 transition-all duration-200 disabled:opacity-50 shadow-sm">
                {loading ? "Sending..." : "Verify email"}
              </button>
              <button type="button" onClick={handleCreateAccount}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md">
                Continue
              </button>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { label: "First name", name: "firstName", icon: <User className="w-4 h-4" />, required: true },
              { label: "Middle name", name: "middleName", icon: <User className="w-4 h-4" />, required: false },
              { label: "Last name", name: "lastName", icon: <User className="w-4 h-4" />, required: true },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">{f.icon}</span>
                  <input name={f.name} placeholder={`Enter ${f.label.toLowerCase()}`}
                    value={formData[f.name]}
                    onChange={(e) => setFormData({ ...formData, [f.name]: e.target.value })}
                    className={`${inputClass} pl-10`} />
                </div>
              </div>
            ))}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Gender <span className="text-red-500">*</span></label>
              <select name="gender" value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className={inputClass}>
                <option value="">Select gender</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
                <option value="Prefer Not To Say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">ID number <span className="text-red-500">*</span></label>
              <div className="relative">
                <IdCard className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="idNumber" placeholder="Enter ID number" value={formData.idNumber}
                  onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                  className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="email" value={formData.createEmail} readOnly className={`${inputClass} pl-10 bg-gray-50 cursor-not-allowed`} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone number <span className="text-red-500">*</span></label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="phone" placeholder="0XX XXX XXXX" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Student number <span className="text-red-500">*</span></label>
              <div className="relative">
                <BookOpen className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="studentNumber" placeholder="Enter student number" value={formData.studentNumber}
                  onChange={(e) => setFormData({ ...formData, studentNumber: e.target.value })}
                  className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Medical conditions</label>
              <div className="relative">
                <Heart className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea name="medicalConditions" rows="3" placeholder="Any medical conditions we should know about? (optional)"
                  value={formData.medicalConditions}
                  onChange={(e) => setFormData({ ...formData, medicalConditions: e.target.value })}
                  className={`${inputClass} pl-10 resize-none`} />
              </div>
            </div>
          </div>
        );
      case 2:
        if (!isLoaded) return (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500 text-sm">Loading address lookup...</p>
            </div>
          </div>
        );
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Street address <span className="text-red-500">*</span></label>
              <div className="relative">
                <Home className="absolute left-3 top-3.5 w-4 h-4 text-gray-400 z-10" />
                <Autocomplete
                  onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                  onPlaceChanged={() => {
                    const place = autocompleteRef.current.getPlace();
                    if (place?.formatted_address) {
                      setFormData((prev) => ({ ...prev, streetAddress: place.formatted_address }));
                    }
                  }}
                >
                  <input type="text" placeholder="Start typing your address..."
                    className={`${inputClass} pl-10`}
                    defaultValue={formData.streetAddress}
                    onChange={(e) => setFormData((prev) => ({ ...prev, streetAddress: e.target.value }))} />
                </Autocomplete>
              </div>
              <p className="text-xs text-gray-500 mt-2 ml-1">Start typing and select from the suggestions</p>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">University <span className="text-red-500">*</span></label>
              <div className="relative">
                <Building className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <select name="university" value={formData.university}
                  onChange={(e) => { setFormData({ ...formData, university: e.target.value }); fetchResidences(e.target.value); }}
                  className={`${inputClass} pl-10`}>
                  <option value="">Select university</option>
                  {Universities.map((uni) => (
                    <option key={uni.universityId} value={uni.universityId}>{uni.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Residence <span className="text-red-500">*</span></label>
              <div className="relative">
                <Home className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <select name="Residence" value={formData.Residence}
                  onChange={(e) => setFormData({ ...formData, Residence: e.target.value })}
                  className={`${inputClass} pl-10`} disabled={!formData.university}>
                  <option value="">Select residence</option>
                  {residences.map((res) => (
                    <option key={res.residenceId} value={res.residenceId}>{res.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Field of study <span className="text-red-500">*</span></label>
              <div className="relative">
                <BookOpen className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="fieldOfStudy" placeholder="e.g., Computer Science" value={formData.fieldOfStudy}
                  onChange={(e) => setFormData({ ...formData, fieldOfStudy: e.target.value })}
                  className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Year of study <span className="text-red-500">*</span></label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input name="yearOfStudy" placeholder="e.g., 1st Year, 2nd Year" value={formData.yearOfStudy}
                  onChange={(e) => setFormData({ ...formData, yearOfStudy: e.target.value })}
                  className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment method</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <select name="paymentMethod" value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  className={`${inputClass} pl-10`}>
                  <option value="">Select payment method</option>
                  <option value="NSFAS">NSFAS</option>
                  <option value="Cash">Cash</option>
                  <option value="Bursary">Bursary</option>
                </select>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { label: "First name", name: "kinFirstName", icon: <User className="w-4 h-4" />, required: true },
              { label: "Last name", name: "kinLastName", icon: <User className="w-4 h-4" />, required: true },
              { label: "Email address", name: "kinEmail", type: "email", icon: <Mail className="w-4 h-4" />, required: true },
              { label: "Phone number", name: "kinPhone", icon: <Phone className="w-4 h-4" />, required: true },
              { label: "ID number", name: "kinIdNumber", icon: <IdCard className="w-4 h-4" />, required: true },
              { label: "Relationship", name: "kinRelationship", icon: <Users className="w-4 h-4" />, required: true, placeholder: "e.g., Parent, Guardian" },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">{f.icon}</span>
                  <input name={f.name} type={f.type || "text"} placeholder={f.placeholder || `Enter ${f.label.toLowerCase()}`}
                    value={formData[f.name]}
                    onChange={(e) => setFormData({ ...formData, [f.name]: e.target.value })}
                    className={`${inputClass} pl-10`} />
                </div>
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Home address</label>
              <div className="relative">
                <Home className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea name="kinHomeAddress" rows="3" placeholder="Enter full address"
                  value={formData.kinHomeAddress}
                  onChange={(e) => setFormData({ ...formData, kinHomeAddress: e.target.value })}
                  className={`${inputClass} pl-10 resize-none`} />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 w-full max-w-3xl">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 px-6 py-8 text-white">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16" />
            <div className="relative z-10 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white overflow-hidden ring-4 ring-white/30 shadow-lg flex-shrink-0 p-1.5">
                <img src="/LOGOSTAY.png" alt="MSA Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Student registration</h1>
                <p className="text-blue-100 text-sm mt-0.5">MSA Student Accommodation Portal</p>
              </div>
            </div>

            {/* Step indicator in header */}
            <div className="relative z-10 flex items-center gap-2 mt-6">
              {tabs.map((tab, index) => {
                const isCompleted = index < activeTab;
                const isActive = index === activeTab;
                return (
                  <React.Fragment key={index}>
                    <button
                      onClick={() => index === 0 || isEmailVerified ? setActiveTab(index) : null}
                      disabled={index > 0 && !isEmailVerified}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                        isActive
                          ? "bg-white text-indigo-700 shadow-md"
                          : isCompleted
                          ? "bg-white/20 text-white"
                          : "bg-white/10 text-white/50 cursor-not-allowed"
                      }`}
                    >
                      {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : tab.icon}
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                    {index < tabs.length - 1 && (
                      <div className={`flex-1 h-0.5 rounded ${index < activeTab ? "bg-white/60" : "bg-white/20"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Form body */}
          <div className="p-6 md:p-8">
            {/* Step title */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-indigo-600">{tabs[activeTab].icon}</span>
                <h2 className="text-base font-bold text-gray-900">
                  {["Create your account", "Personal information", "Your address", "University & residence", "Emergency contact"][activeTab]}
                </h2>
              </div>
              <p className="text-sm text-gray-500">
                {[
                  "Start with your email and a secure password. Verify your email before continuing.",
                  "Tell us a bit about yourself so we can personalise your experience.",
                  "We need your home address for residence administration purposes.",
                  "Let us know where you study and which residence you'd like.",
                  "Provide an emergency contact who we can reach in case of any issues.",
                ][activeTab]}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {renderTabContent()}

              {/* Progress bar */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500">Step {activeTab + 1} of {tabs.length}</span>
                  <span className="text-xs font-semibold text-indigo-600">{progressPercent}% complete</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setActiveTab((prev) => Math.max(prev - 1, 0))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition ${activeTab === 0 ? "invisible" : ""}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                {activeTab > 0 && activeTab < tabs.length - 1 && (
                  <button type="button" onClick={handleNext}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md">
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}

                {activeTab === tabs.length - 1 && (
                  <button type="submit"
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 text-white text-sm font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-md">
                    Submit registration
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 border-t border-gray-100 flex items-center justify-center gap-2">
            <Shield className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-xs text-gray-500">Your information is encrypted and securely stored</span>
          </div>
        </div>

        <p className="text-center text-xs text-white/70 mt-4">© 2024 MSA Student Portal · All rights reserved</p>
      </div>

      <ErrorPopup errors={Object.values(errors)} onClose={() => setShowErrorPopup(false)} show={showErrorPopup} />
      <SuccessPopup message={successMessage} show={showSuccessPopup} onClose={() => setShowSuccessPopup(false)} />
    </div>
  );
}